import React from "react";

import { Box } from "@material-ui/core";
import clsx from "clsx";
import { debounce } from "lodash-es";
import { RouteComponentProps } from "react-router";

import {
  AmenitiesSelection,
  GenericDropdown,
  HotelPriceSelection,
  Icon,
  IconName,
  StarRatingsSelection,
  getCurrencySymbol,
  mapAmenitiesToTopAmenities,
  useDeviceTypes,
} from "halifax";
import {
  AmenityEnum,
  APPLIED_PREMIUM_SEARCH_FILTER,
  HotelStarRatingEnum,
  PremiumStaysFilterType,
  StayTypesEnum,
  VacationRentalAmenityKindEnum,
  hotelStarRatingToNumber,
} from "redmond";

import { initialFilterState } from "../../reducer";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { AMENITY_LABELS } from "./components/AmenitiesSelection/textConstants";
import { RoomCountFilter } from "./components/RoomCountFilter";
import { AvailabilityFilterConnectorProps } from "./container";
import * as textConstants from "./textConstants";

import "./styles.scss";

export interface IAvailabilityFilterProps
  extends AvailabilityFilterConnectorProps,
    RouteComponentProps {}

export const AvailabilityFilter = (props: IAvailabilityFilterProps) => {
  const {
    stayType,
    currency,
    vacationRentalsAmenities,
    setVacationRentalsAmenities,
    amenities,
    setAmenitiesFilter,
    starRatings,
    setStarRatingsFilter,
    maxPrice,
    setMaxPriceFilter,
    minMaxPriceRange,
    vacationRentalsRoomCounts,
    setVacationRentalsRoomCounts,
    hotelName,
    setHotelNameFilter,
    hotelsOnSale,
    setHotelsOnSaleFilter,
    freeCancellation,
    setCancellationFilter,
    appliedFilterTrackingProperties,
  } = props;
  const { matchesDesktop } = useDeviceTypes();

  // local state values
  const [localMaxPriceFilter, setLocalMaxPriceFilter] =
    React.useState<number>(maxPrice);
  const maxPriceSet = maxPrice !== initialFilterState.maxPrice;

  // Use ref to always get latest tracking properties without recreating debounced function
  const appliedFilterTrackingPropertiesRef = React.useRef(
    appliedFilterTrackingProperties
  );
  React.useEffect(() => {
    appliedFilterTrackingPropertiesRef.current = appliedFilterTrackingProperties;
  }, [appliedFilterTrackingProperties]);

  // Track price changes only when user stops sliding (with longer debounce)
  const trackMaxPriceChange = React.useMemo(
    () =>
      debounce((price: number) => {
        trackEvent({
          eventName: APPLIED_PREMIUM_SEARCH_FILTER,
          properties: {
            ...appliedFilterTrackingPropertiesRef.current,
            filter_type: PremiumStaysFilterType.MaxPrice,
            max_price: price,
          },
        });
      }, 200), // Track after user stops sliding for 200ms
    [] // Empty deps - debounced function created once
  );

  // outer state handlers
  const setMaxPriceFilterHandler = React.useCallback(
    (maxPriceVal: number) => {
      setMaxPriceFilter(maxPriceVal);
      trackMaxPriceChange(maxPriceVal); // Track separately with longer debounce
    },
    [setMaxPriceFilter, trackMaxPriceChange]
  );
  const setMaxPriceFilterDebounced = React.useMemo(
    () => debounce(setMaxPriceFilterHandler, 150),
    [setMaxPriceFilterHandler]
  );

  // Wrapper functions with tracking
  const handleSetStarRatingsFilter = (ratings: HotelStarRatingEnum[]) => {
    setStarRatingsFilter(ratings);
    trackEvent({
      eventName: APPLIED_PREMIUM_SEARCH_FILTER,
      properties: {
        ...appliedFilterTrackingProperties,
        filter_type: PremiumStaysFilterType.Rating,
        rating: ratings.map(hotelStarRatingToNumber),
      },
    });
  };

  const handleSetAmenitiesFilter = (amenitiesList: AmenityEnum[]) => {
    setAmenitiesFilter(amenitiesList);
    trackEvent({
      eventName: APPLIED_PREMIUM_SEARCH_FILTER,
      properties: {
        ...appliedFilterTrackingProperties,
        filter_type: PremiumStaysFilterType.Amenities,
        amenities: amenitiesList as string[],
      },
    });
  };

  const handleSetVacationRentalsAmenities = (
    amenitiesList: VacationRentalAmenityKindEnum[]
  ) => {
    setVacationRentalsAmenities(amenitiesList);
    trackEvent({
      eventName: APPLIED_PREMIUM_SEARCH_FILTER,
      properties: {
        ...appliedFilterTrackingProperties,
        filter_type: PremiumStaysFilterType.Amenities,
        amenities: amenitiesList as string[],
      },
    });
  };

  const handleSetCancellationFilter = (value: boolean) => {
    setCancellationFilter(value);
    trackEvent({
      eventName: APPLIED_PREMIUM_SEARCH_FILTER,
      properties: {
        ...appliedFilterTrackingProperties,
        filter_type: PremiumStaysFilterType.FreeCancellation,
        free_cancellation: value,
      },
    });
  };

  const handleSetHotelNameFilter = (name: string) => {
    setHotelNameFilter(name);
    trackEvent({
      eventName: APPLIED_PREMIUM_SEARCH_FILTER,
      properties: {
        ...appliedFilterTrackingProperties,
        filter_type: PremiumStaysFilterType.HotelName,
        hotel_name: name,
      },
    });
  };

  React.useEffect(() => {
    setLocalMaxPriceFilter(maxPrice);
  }, [maxPrice]);

  const hasChangedStarRatingsFilter = React.useMemo(
    () => starRatings.length > 0,
    [starRatings.length]
  );
  const ratingLabel = React.useMemo(() => {
    if (starRatings.length === 0) {
      return "";
    } else {
      const moreCount = starRatings.length - 1;
      return `${textConstants.STAR_RATING_FILTER_LABEL(starRatings[0])}${
        moreCount > 0 ? ` +${moreCount}` : ""
      }`;
    }
  }, [starRatings]);

  const hasChangedAmenitiesFilter = React.useMemo(
    () => amenities.length > 0,
    [amenities.length]
  );
  const topAmenities = React.useMemo(
    () => mapAmenitiesToTopAmenities(amenities),
    [amenities]
  );
  const amenitiesLabel = React.useMemo(() => {
    if (topAmenities.length === 0) {
      return "";
    } else {
      const moreCount = topAmenities.length - 1;
      return `${topAmenities[0].label}${moreCount > 0 ? ` +${moreCount}` : ""}`;
    }
  }, [topAmenities]);

  const vrAmenitiesLabel = React.useMemo(() => {
    if (vacationRentalsAmenities.length > 0) {
      const moreCount = vacationRentalsAmenities.length - 1;
      const label = `${AMENITY_LABELS[vacationRentalsAmenities[0]]}${
        moreCount > 0 ? ` +${moreCount}` : ""
      }`;
      console.log(vacationRentalsAmenities, AMENITY_LABELS, label);
      return label;
    }
    return null;
  }, [vacationRentalsAmenities]);

  const renderDropdownButtons = () => {
    switch (stayType) {
      case StayTypesEnum.VacationRentals:
        return (
          <>
            <RoomCountFilter
              roomCounts={vacationRentalsRoomCounts}
              setVacationRentalsRoomCounts={setVacationRentalsRoomCounts}
              appliedFilterTrackingProperties={appliedFilterTrackingProperties}
            />
            {vrAmenitiesLabel && (
              <EnabledFilterTag
                disableFilter={() => handleSetVacationRentalsAmenities([])}
              >
                <strong>
                  {textConstants.AMENITIES_FILTER_LABEL}
                  {": "}
                </strong>
                {vrAmenitiesLabel}
              </EnabledFilterTag>
            )}
          </>
        );
      case StayTypesEnum.Hotels:
        return (
          <>
            <GenericDropdown
              buttonClassName={clsx(
                "hotel-availability-dropdown",
                "star-ratings",
                "b2b-shop-filter",
                "enhanced-hotel-filters",
                {
                  "has-value": hasChangedStarRatingsFilter,
                }
              )}
              popoverClassName={clsx(
                "hotel-availability-star-ratings-dropdown-popover",
                "b2b",
                "enhanced-hotel-filters"
              )}
              ariaLabel={`${textConstants.RATING_TEXT} filter`}
              dropdownLabel={
                <>
                  <strong>
                    {textConstants.RATING_TEXT}
                    {hasChangedStarRatingsFilter ? ": " : ""}
                  </strong>
                  {hasChangedStarRatingsFilter ? ratingLabel : ""}
                </>
              }
              dropdownIcon={
                hasChangedStarRatingsFilter ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetStarRatingsFilter([]);
                    }}
                  >
                    <Icon name={IconName.XCircle} />
                  </div>
                ) : undefined
              }
              dropdownContent={
                <StarRatingsSelection
                  className="desktop-hotel-availability-star-ratings"
                  starRatings={starRatings}
                  setStarRatings={handleSetStarRatingsFilter}
                />
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            />
            <GenericDropdown
              buttonClassName={clsx(
                "hotel-availability-dropdown",
                "amenities",
                "b2b-shop-filter",
                "enhanced-hotel-filters",
                {
                  "has-value": hasChangedAmenitiesFilter,
                }
              )}
              popoverClassName={clsx(
                "hotel-availability-amenities-dropdown-popover",
                "b2b",
                "enhanced-hotel-filters"
              )}
              ariaLabel={`${textConstants.AMENITIES_FILTER_LABEL} filter`}
              dropdownLabel={
                <>
                  <strong>
                    {textConstants.AMENITIES_FILTER_LABEL}
                    {hasChangedAmenitiesFilter ? ": " : ""}
                  </strong>
                  {hasChangedAmenitiesFilter ? amenitiesLabel : ""}
                </>
              }
              dropdownIcon={
                hasChangedAmenitiesFilter ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetAmenitiesFilter([]);
                    }}
                  >
                    <Icon name={IconName.XCircle} />
                  </div>
                ) : undefined
              }
              dropdownContent={
                <AmenitiesSelection
                  amenities={amenities}
                  setAmenities={handleSetAmenitiesFilter}
                  useTopAmenities
                />
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            />
            <GenericDropdown
              buttonClassName={clsx(
                "premier-collection-availability-dropdown",
                "rounded",
                "b2b-shop-filter",
                { "has-value": maxPriceSet }
              )}
              popoverClassName={clsx(
                "premier-collection-availability-price-dropdown-popover",
                "b2b"
              )}
              ariaLabel={`${textConstants.PRICE} filter`}
              dropdownLabel={
                <>
                  <strong>
                    {textConstants.PRICE}
                    {maxPriceSet ? ":" : ""}
                  </strong>
                  {maxPriceSet
                    ? ` ${getCurrencySymbol(currency)}${maxPrice}`
                    : ""}
                </>
              }
              dropdownIcon={
                maxPriceSet ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setMaxPriceFilterHandler(initialFilterState.maxPrice);
                    }}
                  >
                    <Icon name={IconName.XCircle} />
                  </div>
                ) : undefined
              }
              dropdownContent={
                <HotelPriceSelection
                  className="desktop-premier-collection-availability-price"
                  title={textConstants.PRICE_FILTER_TITLE}
                  subtitle={textConstants.PRICE_FILTER_SUBTITLE}
                  setHotelMaxPrice={(maxPrice) => {
                    setLocalMaxPriceFilter(maxPrice);

                    setMaxPriceFilterDebounced(maxPrice);
                  }}
                  hotelPriceRange={minMaxPriceRange || { min: 0, max: 0 }}
                  hotelMaxPrice={localMaxPriceFilter}
                  currencySymbol={getCurrencySymbol(currency)}
                  type="max"
                  alwaysShowTooltip
                  showMinLabel
                  showClearButton={
                    localMaxPriceFilter !== initialFilterState.maxPrice
                  }
                  reset={() =>
                    setMaxPriceFilterHandler(initialFilterState.maxPrice)
                  }
                />
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            />

            {hotelName && (
              <EnabledFilterTag disableFilter={() => handleSetHotelNameFilter("")}>
                <strong>Hotel:</strong> {hotelName}
              </EnabledFilterTag>
            )}
            {hotelsOnSale && (
              <EnabledFilterTag
                disableFilter={() => setHotelsOnSaleFilter(false)}
              >
                Limited-time promotions
              </EnabledFilterTag>
            )}
            {freeCancellation && (
              <EnabledFilterTag
                disableFilter={() => handleSetCancellationFilter(false)}
              >
                Free cancellation
              </EnabledFilterTag>
            )}
          </>
        );
    }
  };

  return <>{matchesDesktop && renderDropdownButtons()}</>;
};

const EnabledFilterTag = ({
  children,
  disableFilter,
}: {
  children: React.ReactNode;
  disableFilter: () => void;
}): JSX.Element => {
  return (
    <Box className={clsx("hotel-filter-pill")}>
      <Box className="filter-label-wrapper">
        <span className="filter-label-text">{children}</span>
      </Box>
      <button onClick={() => disableFilter()}>
        <Icon name={IconName.XCircle} />
      </button>
    </Box>
  );
};
